import { useAppliedStoryDub } from '@src/components/aiVoices/useAppliedStoryDub';
import { useDubbedLanguages } from '@src/components/aiVoices/useDubbedLanguages';
import { usePublishLanguage } from '@src/hooks/usePublishLanguage';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { formatCompactCode, normalizeLangKey } from '@src/utility/bcp47';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

/**
 * Names the voice this publish will deliver, because publishing cannot be taken
 * back and a wrong-language delivered file is the most expensive mistake this
 * feature can make.
 *
 * Two things decide that voice: the page's language picks which
 * language cell is published, and the voice position saved on the timeline
 * decides whether that cell's dub is placed. So both are read here, the voice
 * position through the same hook the Voice control reads, which is what stops
 * the sentence and the control disagreeing on one screen.
 *
 * It shows only when a dubbed voice will really be delivered — a line that is
 * always on screen stops being read, and then it fails on the publish that
 * mattered. A person who never touched the control still sees it, because with
 * no saved position the publish places the caption language's dub anyway.
 */
export const PublishVoiceNotice: React.FC<{
  /**
   * `sentence` for a dialog, which has room to explain; `chip` for the toolbar's
   * single row — the same fact shortened, with the sentence as hover text. A
   * notice nobody can see is not telling them, so the toolbar gets the short
   * form rather than nothing.
   */
  variant?: 'sentence' | 'chip';
}> = observer(({ variant = 'sentence' }) => {
  const videoCreator = useVideoCreatorStore();
  const dubbedLanguages = useDubbedLanguages(videoCreator.storyId ?? null);

  // The language the page is showing, which is the cell the publish delivers
  // (PLAT-12204) — the same read the publish buttons hand to the publish.
  const activeLanguage = usePublishLanguage();
  // Read before any early return, because it is a hook.
  const { appliedDub } = useAppliedStoryDub();
  // The original cell strips every dub and restores the picture's sound, so the
  // delivered file speaks the original voice whatever the timeline is set to.
  if (isOriginalLang(activeLanguage)) return null;
  // The two sides spell the same language differently — a caption language of
  // `ru` against a dub stored under `ru-RU` — so an exact compare silently never
  // matches. Canonicalize, then collapse the redundant region the way the rest
  // of the app does; `pt-BR` and `pt-PT` still stay apart, because their region
  // is not their language.
  const sameLanguage = (a: string, b: string) =>
    formatCompactCode(normalizeLangKey(a)).toLowerCase() ===
    formatCompactCode(normalizeLangKey(b)).toLowerCase();
  // No dub for the caption language means none can be delivered — the publish
  // places only the cell's own language, never one that happens to be applied.
  if (!dubbedLanguages.some(code => sameLanguage(code, activeLanguage))) {
    return null;
  }
  // A chosen Original is honored by the publish. A null language is the
  // never-touched state, not a chosen Original: with no saved position the
  // publish still places the caption language's dub, so the line still shows.
  if (appliedDub.language !== null && appliedDub.mode === 'original') {
    return null;
  }

  const language = formatLanguageCaption(activeLanguage);
  const full =
    `This will publish with the ${language} dubbed voice. ` +
    `To publish the original voice, set the Voice control to Original.`;

  if (variant === 'chip') {
    return (
      <Chip role="status" data-testid="publish-voice-notice" title={full}>
        {language} voice
      </Chip>
    );
  }

  return (
    <Wrapper role="status" data-testid="publish-voice-notice">
      This will publish with the <strong>{language}</strong> dubbed voice. To
      publish the original voice, set the Voice control to Original.
    </Wrapper>
  );
});

const Wrapper = styled.div`
  margin: 8px 0 0;
  font-size: var(--text-xs);
  color: var(--text-muted, var(--foreground));
  line-height: 1.4;
  text-align: left;
`;

/* Sized for the toolbar row: it sits beside Publish without changing its height. */
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 2px 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 4px);
  font-size: var(--text-xs);
  color: var(--foreground);
  cursor: default;
`;
